<template lang="pug">
MModalButton(
  button-classes="btn btn-white"
  modal-size="modal-lg"
  v-bind:title="`Report an issue with this advertiser`"
)
  span( slot="button-inner" )
    | Issue with this advertiser?
  
  div( slot="body" )
    MForm( submit-text="Report Issue" v-on:submit="createIssueReport" v-bind:disabled="creatingIssueReport" )

      h3 Conversion Change

      MInput(
        type="checkbox"
        :name="issueReportFormFields.hasConversionChange.name"
        :label="issueReportFormFields.hasConversionChange.label"
        v-on:change="e => hasConversionChange = e.target.checked"
      )

      MInput(
        type="select"
        :name="issueReportFormFields.conversionChangeAt.name"
        :label="issueReportFormFields.conversionChangeAt.label"
        v-bind:options="issueReportFormFields.conversionChangeAt.options"
        v-bind:disabled="! hasConversionChange"
      )

      MInput(
        type="select"
        name="conversionChangeAmount"
        :label="issueReportFormFields.conversionChangeAmount.label"
        v-bind:options="issueReportFormFields.conversionChangeAmount.options"
        v-bind:disabled="! hasConversionChange"
      )

      MInput(
        type="select"
        name="conversionChangeType"
        :label="issueReportFormFields.conversionChangeType.label"
        v-bind:options="issueReportFormFields.conversionChangeType.options"
        v-bind:disabled="! hasConversionChange"
      )

      h3 File Uploads

      MInput(
        type="file"
        name="files"
        label="Upload related files"
        multiple
      )
        p( slot="input-desc" class="mt-3" )
          em.
            Uploaded files will be visible to network and Moonpull employees and may
            be forwarded to the advertiser too. Only attach non commercially
            sensitive information.

      h3 Please explain the issue

      MInput(
        type="textarea"
        name="comment"
        label="Please explain the issue"
        required
      )

      h3 Communication Preferences

      MInput(
        type="checkbox"
        name="shouldEmailOtherPublishers"
        label="Would you like Moonpull to contact other relevant publishers?"
        v-bind:checked="true"
      )

</template>

<script>
import Vue from 'vue'
import { ref } from '@vue/composition-api'
import { api } from '@/api'
import { issueReportFormFields } from '../views/share/IssueReport.form'

export default Vue.extend({
  props: ['auditId', 'advertiserId'],
  setup(props) {
    const { auditId, advertiserId } = props

    if (! auditId || ! advertiserId) {
      throw new Error('MReportIssueButton requires auditId and advertiserId props')
    }

    // Reactive state for form inputs and logic
    const creatingIssueReport = ref(false)
    const hasConversionChange = ref(false)

    /**
     * Create a new Issue Report
     * @param data Form data from the MForm component
     */
    async function createIssueReport(data) {

      if (creatingIssueReport.value) {
        return
      }

      creatingIssueReport.value = true

      try {

        const formData = new FormData()

        // Append each file to the 'files' key in FormData
        if (data.files) {
          for (const file of data.files) {
            formData.append('files', file)
          }
        }

        // Append other fields to FormData (if any)
        formData.append('comment', data.comment)
        formData.append('auditId', auditId)
        formData.append('advertiserId', advertiserId)

        // Build a bespoke metadata object for display on the UI and Email
        const metadata = {
          comment: {
            label: issueReportFormFields.comment.label,
            value: data.comment 
          },
          conversionChangeAmount: {
            label: issueReportFormFields.conversionChangeAmount.label,
            value: (issueReportFormFields.conversionChangeAmount.options.find(opt => opt.value === data.conversionChangeAmount) || {}).label
          },
          hasConversionChange: {
            label: issueReportFormFields.hasConversionChange.label,
            value: data.hasConversionChange ? 'Yes' : 'No'
          },
          conversionChangeType: {
            label: issueReportFormFields.conversionChangeType.label,
            value: (issueReportFormFields.conversionChangeType.options.find(opt => opt.value === data.conversionChangeType) || {}).label
          },
          conversionChangeAt: {
            label: issueReportFormFields.conversionChangeAt.label,
            value: (issueReportFormFields.conversionChangeAt.options.find(opt => opt.value === data.conversionChangeAt) || {}).label
          },
          shouldEmailOtherPublishers: {
            label: issueReportFormFields.shouldEmailOtherPublishers.label,
            value: data.shouldEmailOtherPublishers ? 'Yes' : 'No'
          }
        }
        formData.append('metadata', JSON.stringify(metadata))

        // Make the API call
        const res = await api('POST', '/issues/reports', formData)

        if (! res.ok) {
          throw new Error(await res.text())
        }

        // Show success message
        this.$emit('dismissModals')
        alert('Issue report created successfully!')
      } catch (error) {
        console.error('Error creating issue report:', error)
        alert(`Error: ${error.message}`)
      } finally {
        creatingIssueReport.value = false
      }
    }

    // Return all properties and methods used in the template
    return {
      creatingIssueReport,
      hasConversionChange,
      issueReportFormFields,
      createIssueReport,
    }
  },
})
</script>
